import {
  FreeDeliveryConfigurationV2,
  LogisticConfiguration,
  Partner,
} from "@dashboard/common/types";
import { camelizeKeys } from "humps";
export type DeliverySettingsForm = {
  customDeliveryEnabled: boolean;
  customDeliveryValue: number;
  nationalLogisticEnabled: boolean;
  nationalDeliveryValue: number;
  storeDelivery: boolean;
  homeDelivery: boolean;
  localFreeEnabled: boolean;
  localFreeMin: number;
  nationalFreeEnabled: boolean;
  nationalFreeMin: number;
  estimatedDeliveryTimeId: string;
  nationalEstimatedDeliveryTimeId: string;
};

export const defaultLogisticConfiguration = (): LogisticConfiguration => ({
  logisticEnabled: false,
  customDeliveryEnabled: false,
  customDeliveryValue: 0,
  customDeliveryMinValue: 0,
  customDeliveryMaxValue: 0,
  localLogisticEnabled: false,
  nationalLogisticEnabled: false,
  localAvailabilityCities: [],
  freeDeliveryConfigurationV2: {
    localLogisticEnabled: false,
    localOrderMinAmountValue: 0,
    nationalLogisticEnabled: false,
    nationalOrderMinAmountValue: 0,
  },
});

export const mergeLogisticConfiguration = (
  ...sources: Array<LogisticConfiguration | undefined>
): LogisticConfiguration => {
  const base = defaultLogisticConfiguration();
  const merged = sources.reduce<LogisticConfiguration>(
    (acc, source) => ({
      ...acc,
      ...source,
      localAvailabilityCities:
        source?.localAvailabilityCities ?? acc.localAvailabilityCities,
      freeDeliveryConfigurationV2: {
        ...base.freeDeliveryConfigurationV2,
        ...acc.freeDeliveryConfigurationV2,
        ...source?.freeDeliveryConfigurationV2,
      },
    }),
    base
  );
  return merged;
};

/** PUT/GET user-detail may return the partner root or a wrapped body. */
export const parseUserDetailResponse = (responseJson: unknown): Partner => {
  const camelized = camelizeKeys(responseJson) as Record<string, unknown>;
  const nested =
    camelized.userDetail ?? camelized.user_detail ?? camelized.userdetail;

  if (nested && typeof nested === "object") {
    return nested as Partner;
  }

  return camelized as Partner;
};

/** Prefer payload + previous detail when API omits nested logistic fields. */
export const mergeUserDetailAfterSave = (
  previous: Partner,
  payload: Partner,
  response: Partner
): Partner => ({
  ...previous,
  ...response,
  storeDelivery: payload.storeDelivery,
  homeDelivery: payload.homeDelivery,
  logisticConfiguration: mergeLogisticConfiguration(
    previous.logisticConfiguration,
    response.logisticConfiguration,
    payload.logisticConfiguration
  ),
});

export const mapPartnerToDeliverySettingsForm = (
  detail: Partner
): DeliverySettingsForm => {
  const lc = mergeLogisticConfiguration(detail.logisticConfiguration);
  const free = lc.freeDeliveryConfigurationV2;

  return {
    customDeliveryEnabled: !!lc.customDeliveryEnabled,
    customDeliveryValue: lc.customDeliveryValue ?? 0,
    nationalLogisticEnabled: !!lc.nationalLogisticEnabled,
    nationalDeliveryValue: lc.nationalDeliveryValue ?? 0,
    storeDelivery: !!detail.storeDelivery,
    homeDelivery: !!detail.homeDelivery,
    localFreeEnabled: !!free?.localLogisticEnabled,
    localFreeMin: free?.localOrderMinAmountValue ?? 0,
    nationalFreeEnabled: !!free?.nationalLogisticEnabled,
    nationalFreeMin: free?.nationalOrderMinAmountValue ?? 0,
    estimatedDeliveryTimeId: lc.estimatedDeliveryTimeId || "",
    nationalEstimatedDeliveryTimeId: lc.nationalEstimatedDeliveryTimeId || "",
  };
};
